import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Download } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const WORKFLOW = [
  ["IPO Accounts", "Create and maintain the investment accounts used for IPO applications. For a new installation, begin here — every application must be linked to an account."],
  ["IPO Applications", "Record each IPO application against the appropriate account, including issue details, quantity and application amount."],
  ["Allotment / Listing", "Update allotment results and listing information as they become available. Allotted quantities and listing prices feed the P/L engine."],
  ["Profit / Loss", "Review realized and unrealized IPO performance per application, per account and across the portfolio."],
  ["Analytics", "Review consolidated portfolio and IPO performance intelligence — trends, hit rates and account comparisons."],
  ["Reports", "Generate operational, monthly, yearly and executive reports for record-keeping and review."],
  ["Export", "Export your data for use outside the suite — spreadsheets, filings or archives."],
  ["Backup / Restore", "Take regular backups of your database and restore from a backup whenever needed."],
];

const MODULES = [
  ["Dashboard", "At-a-glance overview of accounts, live applications, recent allotments and headline P/L."],
  ["IPO Accounts", "Manage the investor accounts (self, family, entities) that apply for IPOs."],
  ["IPO Applications", "Full application register — issue, account, quantity, amount and status."],
  ["Allotment / Listing", "Record allotment outcomes and listing-day data for each application."],
  ["Profit / Loss", "Realized and unrealized P/L computed from allotment and listing inputs."],
  ["Analytics", "Consolidated intelligence across accounts, issues and time periods."],
  ["Reports", "Operational, monthly, yearly and executive reporting."],
  ["Export", "Structured data export for external analysis and archiving."],
  ["Backup / Restore", "One-click database backup and restore for data protection."],
  ["Settings & Themes", "Configure preferences and switch between light and dark modes."],
  ["License", "View and manage your YIIS-100 license details."],
  ["Windows GUI", "Native Windows desktop experience — Refresh, Restart and Exit controls in the title bar."],
];

const FAQS = [
  ["The status bar shows \"Database: Ready\" — what does it mean?", "Your local database is connected and healthy. If it shows anything else, use Restart from the top bar, or restore your latest backup from Backup / Restore."],
  ["My data looks stale after an edit.", "Use the Refresh button in the top bar to reload all modules from the database."],
  ["Where is my data stored?", "All data is stored locally on your machine in the YIIS-100 database. Nothing is uploaded — take regular backups via Backup / Restore to protect it."],
  ["How do I move YIIS-100 to a new computer?", "Take a backup on the old machine, install YIIS-100 on the new one, then use Restore to bring your entire database across."],
  ["The app misbehaves after an update.", "Use Restart from the top bar first. If the issue persists, restore your latest backup and check Release History for version notes."],
];

export default function Help() {
  useEffect(() => {
    document.documentElement.classList.toggle(
      "dark",
      (localStorage.getItem("yiis-theme") || "dark") === "dark"
    );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="help-page">
      <header className="fixed top-0 inset-x-0 z-50 border-b border-border bg-white/70 dark:bg-black/60 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a
            href="/"
            data-testid="help-back-link"
            className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to site
          </a>
          <span className="flex items-center gap-2.5">
            <img src="/logo.png" alt="YIIS-100 logo" className="w-7 h-7 rounded-md" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
              YIIS-100 / USER GUIDE
            </span>
          </span>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-12 pt-32 pb-24">
        <motion.div {...reveal}>
          <div className="flex items-center gap-3 mb-6">
            <BookOpen size={20} className="text-accent" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent">
              Help &amp; Documentation
            </span>
          </div>
          <h1 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="help-heading">
            YIIS-100 USER GUIDE
          </h1>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl">
            User guidance, recommended workflows, data protection information and
            troubleshooting for YIIS-100 — IPO Intelligence Edition, v1.0 "Operation BLUE".
          </p>
        </motion.div>

        {/* Getting started */}
        <motion.section {...reveal} className="mt-20" data-testid="help-getting-started">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
            01 / Getting Started
          </div>
          <div className="border border-border p-8 md:p-10">
            <h2 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-lg mb-4">
              YIIS-100 — IPO Intelligence Edition
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              YIIS-100 is designed to manage the complete lifecycle of IPO activity — from
              account setup and application recording through allotment, listing,
              profit/loss analysis and reporting.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              For a new installation, begin by creating the IPO investment accounts that
              will be used for IPO applications. Everything else in the suite builds on
              those accounts.
            </p>
          </div>
        </motion.section>

        {/* Workflow */}
        <motion.section {...reveal} className="mt-20" data-testid="help-workflow">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
            02 / Recommended Workflow
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {WORKFLOW.map(([title, desc], i) => (
              <div key={title} className="bg-background p-8" data-testid={`workflow-step-${i + 1}`}>
                <div className="font-mono text-xs tracking-[0.2em] text-accent mb-3 tabular-nums">
                  STEP {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-base mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Modules */}
        <motion.section {...reveal} className="mt-20" data-testid="help-modules">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
            03 / Module Reference
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {MODULES.map(([title, desc]) => (
              <div key={title} className="bg-background p-7">
                <h3 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-sm mb-2">{title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Data protection */}
        <motion.section {...reveal} className="mt-20" data-testid="help-data-protection">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
            04 / Data Protection
          </div>
          <div className="border border-border p-8 md:p-10">
            <p className="text-muted-foreground leading-relaxed mb-4">
              Your entire portfolio lives in a local database on your machine — YIIS-100
              never uploads your financial data anywhere. The status bar confirms database
              health with <span className="text-accent font-mono text-sm">Database: Ready</span>.
            </p>
            <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
              <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-accent mt-1.5 shrink-0" /> Take a backup from Backup / Restore before major data entry sessions or updates.</li>
              <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-accent mt-1.5 shrink-0" /> Store backup files on a second drive or cloud folder of your choice.</li>
              <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-accent mt-1.5 shrink-0" /> Restore replaces the current database with the selected backup — back up first if unsure.</li>
            </ul>
          </div>
        </motion.section>

        {/* Troubleshooting */}
        <motion.section {...reveal} className="mt-20" data-testid="help-troubleshooting">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-6">
            05 / Troubleshooting
          </div>
          <div className="flex flex-col gap-px bg-border border border-border">
            {FAQS.map(([q, a], i) => (
              <div key={i} className="bg-background p-8" data-testid={`faq-item-${i + 1}`}>
                <h3 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-base mb-2">{q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...reveal} className="mt-20 border border-border p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="help-cta">
          <div>
            <h2 className="font-display font-semibold tracking-tight [word-spacing:0.1em] text-xl mb-2">
              Ready to run Operation BLUE?
            </h2>
            <p className="text-sm text-muted-foreground">
              Download YIIS-100 for Windows and start tracking your IPO portfolio.
            </p>
          </div>
          <a
            href="/#download"
            data-testid="help-download-link"
            className="w-fit flex items-center gap-2 px-6 py-3 bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase hover:bg-accent hover:text-accent-foreground transition-colors duration-300"
          >
            <Download size={14} /> Get YIIS-100
          </a>
        </motion.section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          © {new Date().getFullYear()} YKD ENTERPRISES — YIIS-100 IPO INTELLIGENCE EDITION
        </div>
      </footer>
    </div>
  );
}
